import React, { useState, useEffect, useMemo, useRef } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css'
import {sdk} from './sdk.js';
import {sdkLocal} from './sdkLocal.js';
import './TiktokVideo.css';
import ReactPlayer from 'react-player';
import { TiktokSidebar } from './TiktokSidebar';
import { FaVolumeMute, FaVolumeUp } from 'react-icons/fa';

const useElementOnScreen = (options, targetRef) => {
  const [isVisible, setIsVisible] = useState(false);
  
  const callbackFunction = (entries) => {
    const [entry] = entries;
    setIsVisible(entry.isIntersecting);
  };
  
  const optionsMemo = useMemo(() => options, [options]);
  
  useEffect(() => {
    const observer = new IntersectionObserver(callbackFunction, optionsMemo);
    const currentTarget = targetRef.current;
    
    if (currentTarget) observer.observe(currentTarget);
    
    return () => {
      if (currentTarget) observer.unobserve(currentTarget);
      observer.disconnect();
    };
  }, [targetRef, optionsMemo]);
  
  return isVisible;
};

const TiktokVideo = ({ user, video, seeingVideo, isAdmin, adHeight = 100 }) => {
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [hasLiked, setHasLiked] = useState(false);
  const [timeListen, setTimeListen] = useState(0);
  const [timePercent, setTimePercent] = useState(0);
  const [isBuffering, setIsBuffering] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);

  // Use the CDN URL directly for better performance
  const currentVideoUrl = useMemo(
    () => `https://peupleqc.com/s/peupleqc-upload/${video.postFile}`,
    [video.postFile]
  );

  const isVisible = useElementOnScreen(
    { 
      root: null, 
      rootMargin: '0px', 
      threshold: 0.7 // Increased threshold for better viewing experience
    },
    videoRef
  );

  // Handle video play/pause state
  useEffect(() => {
    if (!video) return;
    
    if (isVisible) {
      if (typeof seeingVideo === 'function') seeingVideo(video.id);
      setPlaying(true);
    } else {
      if (playing) {
        setPlaying(false);
        // Log and save viewing stats
        console.log(`Video Stopped: ${video.id}, Time: ${timeListen}s, Percent: ${timePercent.toFixed(1)}%`);
        
        if (timeListen > 0) {
          sdk.models.PostsTime.setPostTime({
            post_id: video.id,
            percent: timePercent,
            tempsSec: timeListen,
          });
        }
      }
    }
  }, [isVisible, playing, video, timePercent, timeListen, seeingVideo]);

  // Fetch like status
  useEffect(() => {
    const fetchLike = async () => {
      try {
        const response = await sdk.models.PostsLike.hasLiked({ post_id: video.id });
        setHasLiked(response.hasLiked);
      } catch (error) {
        console.error("Error fetching like status:", error);
      }
    };
    
    if (user && video) {
      fetchLike();
    }
  }, [video, user]);

  // Pause video when comments are open
  useEffect(() => {
    if (commentsOpen && playing) {
      setPlaying(false);
    }
  }, [commentsOpen]);

  const handlePlayPause = () => {
    setPlaying(!playing);
  };

  const handleProgress = ({ playedSeconds, played }) => {
    if (playedSeconds > timeListen) {
      setTimeListen(playedSeconds);
      setTimePercent(played * 100);
    }
  };

  const handleBuffer = (buffering) => {
    setIsBuffering(buffering);
  };

  const toggleMute = (e) => {
    e.stopPropagation(); // Empêcher la propagation pour éviter de déclencher le play/pause
    setIsMuted(!isMuted);
  };

  const openComments = () => {
    setCommentsOpen(true);
    // Pause la vidéo quand les commentaires sont ouverts
    setPlaying(false);
  };

  const closeComments = () => {
    setCommentsOpen(false);
    // Reprend la lecture si la vidéo est visible
    if (isVisible) {
      setPlaying(true);
    }
  };
  
  // Pour être transmise à TiktokSidebar
  const toggleComments = () => {
    if (commentsOpen) {
      closeComments();
    } else {
      openComments();
    }
  };

  // Style pour ajuster la hauteur du conteneur vidéo en fonction de la hauteur de la bannière
  const videoStyle = {
    height: `calc(100vh - ${adHeight}px)`,
  };

  return (
    <div className="video" ref={videoRef} id={`video-container-${video.id}`} style={videoStyle}>
      <div className="video__player-container">
        {isBuffering && (
          <div className="video-buffer-overlay">
            <div className="buffer-spinner"></div>
          </div>
        )}
        
        <ReactPlayer
          ref={playerRef}
          key={`player-${video.id}`}
          url={currentVideoUrl}
          playing={playing}
          loop
          muted={isMuted}
          playsinline
          className="video__player"
          width="100%"
          height="100%"
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onProgress={handleProgress}
          onBuffer={() => handleBuffer(true)}
          onBufferEnd={() => handleBuffer(false)}
          config={{
            file: {
              attributes: {
                preload: 'auto',
                controlsList: 'nodownload'
              }
            }
          }}
        />
        
        <div className="video__overlay" onClick={handlePlayPause}>
          {!playing && (
            <div className="play-button">
              <svg viewBox="0 0 24 24" width="48" height="48" fill="white">
                <path d="M8 5v14l11-7z"/>
              </svg>
            </div>
          )}
        </div>
        
        <div className="video__controls">
          <div className="mute-button" onClick={toggleMute}>
            {isMuted ? (
              <FaVolumeMute size={20} color="white" />
            ) : (
              <FaVolumeUp size={20} color="white" />
            )}
          </div>
        </div>
        
        <div className="video__info">
          <h3>{video.title || `Vidéo de ${video.postBy || 'utilisateur'}`}</h3>
          <p>{video.description || video.postText || ''}</p>
        </div>
      </div>
      
      <TiktokSidebar
        user={user}
        video={video}
        avatar={video.postByImage}
        avatarLink={video.postByLink}
        postId={video.id}
        hasLiked={hasLiked}
        setHasLiked={setHasLiked}
        likes={video.nbrLikes}
        messages={video.comments.length}
        shares={video.nbrShares}
        isAdmin={isAdmin}
        commentWindowOpen={commentsOpen}
        toggleComments={toggleComments}
      />
    </div>
  );
};

export { TiktokVideo };